import { useState, useEffect, useRef } from "react";
import { Input, Button } from "antd";
import { ICON_LIBRARY_NAMES, resolveIconComponent } from "./iconLibrary.constants";

// Shared icon-library picker — a compact button showing the currently
// resolved icon (or a neutral placeholder for an unrecognized/unset value —
// e.g. ourAdvantage's pre-migration raw emoji strings, which never match a
// real AntD name and so render as "unselected", never crash or force a
// bulk-rewrite) that opens a small text-search-by-name dropdown over the
// curated ICON_LIBRARY_NAMES list. Used both as a top-level schema field
// (hero's own icon-typed fields, if any) and inside CardsEditor's per-item
// icon field (hero's features[].icon, ourAdvantage's cards[].icon).
function IconPickerField({ value, onChange, size = "small" }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);
    const ResolvedIcon = resolveIconComponent(value);
    const filteredNames = ICON_LIBRARY_NAMES.filter((name) =>
        name.toLowerCase().includes(search.toLowerCase())
    );

    // Click-outside-to-close: without this, the dropdown stays open
    // indefinitely once opened (no other way to dismiss it without picking
    // an icon), stacking visually over whatever the admin scrolls to next —
    // especially noticeable with many icon pickers in one CardsEditor list.
    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
            <Button
                size={size}
                onClick={() => setOpen((o) => !o)}
                icon={ResolvedIcon ? <ResolvedIcon /> : undefined}
            >
                {!ResolvedIcon && "Pick icon"}
            </Button>
            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20,
                    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: 8, width: 220,
                }}>
                    <Input
                        size="small"
                        placeholder="Search icons..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginBottom: 6 }}
                        autoFocus
                    />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4, maxHeight: 180, overflowY: "auto" }}>
                        {filteredNames.map((name) => {
                            const Icon = resolveIconComponent(name);
                            return (
                                <Button
                                    key={name}
                                    size="small"
                                    title={name}
                                    icon={<Icon />}
                                    type={value === name ? "primary" : "default"}
                                    onClick={() => { onChange(name); setOpen(false); setSearch(""); }}
                                />
                            );
                        })}
                        {filteredNames.length === 0 && (
                            <span style={{ gridColumn: "1 / -1", fontSize: 11, color: "#999", textAlign: "center", padding: "8px 0" }}>
                                No icons match
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export { IconPickerField };
